import { Fragment, useCallback, useState } from 'react';
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';
import { modalStyle } from '../../../../utils/modal';
import { Spinner } from '../../../../components/Spinner';
import Dropdown from 'react-bootstrap/Dropdown';
import Container from 'react-bootstrap/Container';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useEnv } from '../../../../context/env-context';
import { useAuth } from '../../../../hooks/useAuth';
import { makeRequest } from '../../../../utils/makeRequest';
import { REPORTS_DISPLAY_NAMES, REPORTS_GROUPS } from '../../../../constants/reports';
import ReportsAggregatorPeriodModalForm from '../modal-forms/ReportsAggregatorPeriodModalForm';
import ReportsDatesInputModalForm from '../modal-forms/ReportsDatesInputModalForm';
import ReportsNoInputModalForm from '../modal-forms/ReportsNoInputModalForm';
import ReportsAggregatorNameModalForm from '../modal-forms/ReportsAggregatorNameModalForm';
import ReportsPeriodModalForm from '../modal-forms/ReportsPeriodModalForm';
import ReportsAggregatorMonthlyModalForm from '../modal-forms/ReportsAggregatorMonthlyModalForm';
import ReportsAggregatorDateModalForm from '../modal-forms/ReportsAggregatorDateModalForm';
import ReportsSurveysModalForm from '../modal-forms/ReportsSurveysModalForm';
import ReportsDateModalForm from '../modal-forms/ReportsDateModalForm';
import ReportsRewardsLogModalForm from '../modal-forms/ReportsRewardsLogModalForm';
import ReportsRewardsModalForm from '../modal-forms/ReportsRewardsModalForm';
import ReportsAggregatorMetricsModalForm from '../modal-forms/ReportsAggregatorMetricsModalForm';
import ReportProfitabilityModalForm from '../modal-forms/ReportProfitabilityModalForm';
import ReportsDODModalForm from '../modal-forms/ReportsDODModalForm';

const NEW_REPORT_MODAL_FORMS = {
  REPAYMENTS_ANALYTICS: ReportsAggregatorPeriodModalForm,
  CONSUMER_ECONOMICS: ReportsDatesInputModalForm,
  CONSUMER_ANALYTICS: ReportsAggregatorNameModalForm,
  CONSUMER_HUBSPOT: ReportsNoInputModalForm,
  ADVANCES_ANALYTICS: ReportsDatesInputModalForm,
  TRANSFERS_ANALYTICS: ReportsDatesInputModalForm,
  SIGNUP_FUNNEL: ReportsPeriodModalForm,
  GENERIC_SIGNUP: ReportsPeriodModalForm,
  RISK_INDICATORS: ReportsNoInputModalForm,
  DETAILED_SIGNUP_FUNNEL: ReportsAggregatorPeriodModalForm,
  REFERRALS: ReportsPeriodModalForm,
  AGGREGATOR_MONTHLY: ReportsAggregatorMonthlyModalForm,
  REWARDS: ReportsRewardsModalForm,
  LOGINS: ReportsAggregatorPeriodModalForm,
  OUTSTANDING_ADVANCES: ReportsAggregatorDateModalForm,
  ENGAGEMENT: ReportsAggregatorPeriodModalForm,
  SURVEYS: ReportsSurveysModalForm,
  LOSSES: ReportsDateModalForm,
  REWARDS_LOG: ReportsRewardsLogModalForm,
  METRICS: ReportsAggregatorMetricsModalForm,
  PROFITABILITY: ReportProfitabilityModalForm,
  DOD: ReportsDODModalForm,
};

function ReportsCreationDropdown({ reportType, setReportType }) {
  return (
    <Dropdown>
      <Dropdown.Toggle className="bo-dropdown-toggle-alt">
        <p className="p-0 m-0">{REPORTS_DISPLAY_NAMES[reportType] || 'Select Type'}</p>
      </Dropdown.Toggle>
      <Dropdown.Menu className="bo-dropdown-menu-alt bo-h-372 overflow-auto">
        {Object.entries(REPORTS_GROUPS).map(([group, keys]) => (
          <Fragment key={group}>
            <Dropdown.Item
              disabled
              className="bo-dropdown-menu-alt-item bo-dropdown-menu-alt-title"
            >
              {group}
            </Dropdown.Item>
            {keys.map(key => (
              <Dropdown.Item
                key={key}
                className="bo-dropdown-menu-alt-item bo-w-250"
                onClick={() => setReportType(key)}
              >
                {REPORTS_DISPLAY_NAMES[key]}
              </Dropdown.Item>
            ))}
          </Fragment>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function ReportsCreationModal({ isOpen, closeModal, getAll }) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginReports, apiOriginAggregator } = useEnv();

  const [spinner, setSpinner] = useState(false);
  const [reportType, setReportType] = useState('');

  const [aggregators, setAggregators] = useState([]);

  const close = useCallback(() => {
    setReportType('');
    closeModal();
  }, [closeModal]);

  const createReport = useCallback(
    async reportParams => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      try {
        setSpinner(true);

        const config = {
          token,
          url: `${apiOriginReports}/create`,
          method: 'POST',
          data: { type: reportType, params: reportParams },
        };

        await makeRequest(config);

        getAll();
        close();
        toast.success('Report was successfully created');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    },
    [apiOriginReports, getAccessTokenSilently, close, getAll, reportType],
  );

  const getAggregators = useCallback(async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginAggregator}/get`,
      method: 'GET',
      params: {
        sortBy: 'name',
        sortOrder: 'asc',
        statusFilter: '',
        searchText: '',
        page: 0,
        perPage: 1000000,
      },
    };

    try {
      const data = await makeRequest(config);

      setAggregators(data);
    } catch (error) {
      toast.error(`Failed getting aggregators: ${error.message}`);
    }
  }, [apiOriginAggregator, getAccessTokenSilently]);

  const renderReportTypeForm = () => {
    if (!reportType || !NEW_REPORT_MODAL_FORMS[reportType]) {
      return null;
    }

    const ReportModalForm = NEW_REPORT_MODAL_FORMS[reportType];

    return (
      <ReportModalForm
        setSpinner={setSpinner}
        close={close}
        createReport={createReport}
        aggregators={aggregators}
        getAggregators={getAggregators}
      />
    );
  };

  return (
    <ReactModal
      isOpen={isOpen}
      style={modalStyle({ width: '460px' })}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      onRequestClose={close}
    >
      <Spinner visible={spinner} text="">
        <Container className="bo-h-500 d-flex flex-column">
          <div className="d-flex flex-column flex-grow-1">
            <h5>Create New Report</h5>
            <Row className="mt-4 mb-2 align-items-center">
              <Col md={4} className="bo-same-line">
                <Form.Label>Type:</Form.Label>
              </Col>
              <Col md={8} className="bo-same-line">
                <ReportsCreationDropdown reportType={reportType} setReportType={setReportType} />
              </Col>
            </Row>
            {renderReportTypeForm()}
          </div>
          {!reportType && (
            <Row className="mt-auto">
              <Col className="text-center mt-5">
                <Button className="bo-button" onClick={close}>
                  Cancel
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </Spinner>
    </ReactModal>
  );
}

export default ReportsCreationModal;
